// Photos and video of an SOP, in the browser (branch sop-visual): compress on upload (the long edge at most 1600 px, JPEG), check a
// video (at most 30 s, 40 MB), upload into the private bucket under sop_media/<vessel>/<SOP id>/ and hand back the PATH the SOP stores.
// The pure rules (sizes, limits, the path) are in src/lib/sopVisual.js.
import { uploadStored } from './fileAccess'
import { fitSize, IMAGE_MAX_EDGE, videoProblem, mediaExt } from './sopVisual'

// a picture (File / Blob) -> { blob, w, h }: turned upright, scaled down to 1600 px, re-encoded as JPEG
export async function compressImage(file, { max = IMAGE_MAX_EDGE, quality = 0.82 } = {}) {
  const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' })
  const { w, h } = fitSize(bmp.width, bmp.height, max)
  const c = document.createElement('canvas'); c.width = w; c.height = h
  c.getContext('2d').drawImage(bmp, 0, 0, w, h)
  if (bmp.close) bmp.close()
  const blob = await new Promise((res, rej) => c.toBlob(b => (b ? res(b) : rej(new Error('The photo could not be compressed'))), 'image/jpeg', quality))
  return { blob, w, h }
}
export const loadBitmap = async src => createImageBitmap(src, { imageOrientation: 'from-image' })

// the length of a video file in seconds (0 when the browser cannot read it)
export function videoSeconds(file) {
  return new Promise(resolve => {
    const v = document.createElement('video'), url = URL.createObjectURL(file)
    const done = n => { URL.revokeObjectURL(url); resolve(n) }
    v.preload = 'metadata'; v.muted = true
    v.onloadedmetadata = () => done(Number.isFinite(v.duration) ? v.duration : 0)
    v.onerror = () => done(0)
    setTimeout(() => done(0), 8000)
    v.src = url
  })
}

// one photo -> the path stored in the step. `blob` is already compressed (and marked up, when the officer did)
export async function uploadSopPhoto(vesselId, manualId, blob) {
  const path = await uploadStored('sop_media', vesselId, blob, { name: 'photo.' + mediaExt('image/jpeg', 'image'), sub: manualId, contentType: 'image/jpeg' })
  return { path, kind: 'image' }
}
// a video file -> { path, kind, secs }; throws a plain sentence when it is too long / big / not a video
export async function uploadSopVideo(vesselId, manualId, file) {
  const secs = await videoSeconds(file)
  const bad = videoProblem({ type: file.type, size: file.size, secs })
  if (bad) throw new Error(bad)
  const path = await uploadStored('sop_media', vesselId, file, { name: 'video.' + mediaExt(file.type, 'video'), sub: manualId, contentType: file.type })
  return { path, kind: 'video', secs: Math.round(secs) }
}
